<template>
  <StagedPreselectBlock
    :title="title"
    :fields="fields"
    :color-schema="colorSchema"
    :show-full-width="showFullWidth"
    :settings="settings"
    :staged="staged"
  />
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, onMounted } from 'vue';

import { generateUrl } from '@shop/utils/Routing';

import StagedPreselectBlock from './StagedPreselectBlock.vue';
import { useStagedPreselect } from './useStagedPreselect';

import type { SnowChainPreselectBlockProps } from './SnowChainPreselectBlock.types';
import type { PreselectOption } from './StagedPreselectBlock.types';

const props = defineProps<SnowChainPreselectBlockProps>();

const attributes = computed(() => props.fields.map(field => field.name));

const initialOptions = computed(() => {
  const opts: Record<string, PreselectOption[]> = {};
  props.fields.forEach(field => {
    if (field.options?.length) {
      opts[field.name] = field.options;
    }
  });
  return opts;
});

const staged = useStagedPreselect({
  attributes: attributes.value,
  optionsRoute: props.optionsRoute,
  initialOptions: initialOptions.value,
  settings: props.settings,
  onComplete: async () => {
    const tireSizeParts = attributes.value.map(attr => staged.selectedOptions[attr] ?? '0');
    const tireSize = `bandenmaat=${tireSizeParts.join('-')}`;

    let searchParams = window.location.search ? window.location.search.slice(1) : '';
    const filteredSearch = searchParams
      .split('&')
      .filter(
        param => param !== 'dir=desc' && param !== 'limit=80' && !param.includes('bandenmaat')
      );
    searchParams = filteredSearch.length > 0 ? `&${filteredSearch.join('&')}` : '';

    const response = await axios.get<{ url: string }>(generateUrl(props.urlRoute));
    const basicUrl = new URL(response.data.url, window.location.origin);

    const stayOnPage = props.settings?.stay_current_page === true;
    let path = stayOnPage ? window.location.pathname : basicUrl.pathname;

    const hasProductOverview = document.querySelector('product-overview') !== null;
    if (!hasProductOverview) {
      path = basicUrl.pathname;
    }

    window.location.href = `${path}?dir=desc&limit=80&${tireSize}${searchParams}`;
  },
});

onMounted(() => {
  const url = new URL(window.location.href);
  const bandenmaat = url.searchParams.get('bandenmaat');
  if (!bandenmaat) {
    return;
  }

  const values = bandenmaat.split('-');
  attributes.value.reduce<Promise<unknown>>((chain, attr, index) => {
    return chain.then(() =>
      staged.selectFacet({ target: { name: attr, value: values[index] ?? '' } }, true)
    );
  }, Promise.resolve());
});
</script>
